import { Button } from "@newsela/angelou";
import PropTypes from "prop-types";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { rootCss, buttonCss } from "./NavigationButtons.style";
const propTypes = {
  children: PropTypes.node,
  handleClick: PropTypes.func,
  objectId: PropTypes.string,
  contentId: PropTypes.string,
};

const quizNavigationPropTypes = {
  ...propTypes,
  currentQuestion: PropTypes.string,
  isReviewMode: PropTypes.bool,
  isStudent: PropTypes.bool,
  isSummary: PropTypes.bool,
  questionCount: PropTypes.number,
};

const powerWordsNavigationPropTypes = {
  ...propTypes,
  currentQuestion: PropTypes.string,
  isStudent: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  isSummary: PropTypes.bool,
  questionCount: PropTypes.number,
};

const writePromptNavigationPropTypes = {
  ...propTypes,
  handleClick: PropTypes.func,
  hasRelevantWriteResponses: PropTypes.bool,
  isConfirmationScreen: PropTypes.bool,
  isDisabled: PropTypes.bool,
  showBackToClassesButton: PropTypes.bool,
};

const questionFlaggingNavigationPropTypes = {
  ...propTypes,
  activityObjectType: PropTypes.string,
  contentId: PropTypes.string,
  isReportSubmitted: PropTypes.bool,
  questionId: PropTypes.string,
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const NavigationButtons = ({ children }) => {
  return <div css={rootCss}>{children}</div>;
};

const QuizNavigationButtons = ({
  currentQuestion,
  handleClick,
  isReviewMode,
  isStudent,
  isSummary,
  objectId,
  contentId,
  questionCount,
}) => {
  const isOnLastQuestion =
    !isSummary && questionCount && currentQuestion === questionCount.toString();
  const isOnFirstQuestion = currentQuestion === "1";
  const isNonStudentOnLastQuestion = isOnLastQuestion && !isStudent;

  const navigationButtonEventProperties = {
    objectType: objectTypes.quizQuestion,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate through each quiz question.",
      object_id: objectId,
      content_id: contentId,
    },
  };

  const reviewButtonEventProperties = {
    objectType: objectTypes.quizQuestion,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Return to the start of the quiz for review",
      object_id: objectId,
      content_id: contentId,
    },
  };

  return (
    <>
      {isReviewMode && isSummary && (
        <ButtonWithTrackEvent
          {...reviewButtonEventProperties}
          __cssFor={{ root: buttonCss }}
          onClick={() => handleClick("review")}
          flavor={Button.flavors.primary}
        >
          Review questions
        </ButtonWithTrackEvent>
      )}
      {!isSummary && (
        <>
          {!isOnFirstQuestion && (
            <ButtonWithTrackEvent
              {...navigationButtonEventProperties}
              __cssFor={{ root: buttonCss }}
              onClick={() => handleClick("back")}
              disabled={isOnFirstQuestion}
              flavor={Button.flavors.secondary}
            >
              Back
            </ButtonWithTrackEvent>
          )}
          {isNonStudentOnLastQuestion ? null : (
            <ButtonWithTrackEvent
              {...navigationButtonEventProperties}
              streamProperties={
                navigationButtonEventProperties.streamProperties
              }
              __cssFor={{ root: buttonCss }}
              onClick={() =>
                isOnLastQuestion ? handleClick("submit") : handleClick("next")
              }
              flavor={Button.flavors.primary}
            >
              {isOnLastQuestion ? (isReviewMode ? "Review" : "Submit") : "Next"}
            </ButtonWithTrackEvent>
          )}
        </>
      )}
    </>
  );
};

const QuestionFlaggingNavigationButtons = ({
  activityObjectType,
  contentId,
  handleClick,
  isReportSubmitted,
  questionId,
}) => {
  const navigationButtonEventProperties = {
    actionName: actionNames.flag,
    objectType: activityObjectType,
  };

  const navigatonButtonStreamProperties = {
    component_type: "NavigationButton",
    content_id: contentId,
    content_type: "Article",
    object_id: questionId,
    object_type: activityObjectType,
  };

  const cancelButtonEventProperties = {
    ...navigationButtonEventProperties,
    actionPrefix: actionPrefixes.cancel,
    streamProperties: {
      ...navigatonButtonStreamProperties,
      description:
        "Close quiz question flagging form and return to current question",
    },
  };

  const submitButtonEventProperties = {
    ...navigationButtonEventProperties,
    streamProperties: {
      ...navigatonButtonStreamProperties,
      description: "Submit quiz question flagging form",
    },
  };

  const renderReturnButton = () => {
    return (
      <ButtonWithTrackEvent
        {...cancelButtonEventProperties}
        flavor={Button.flavors.primary}
        onClick={() => handleClick("cancelReport")}
      >
        Return to quiz
      </ButtonWithTrackEvent>
    );
  };

  const renderFormOptions = () => {
    return (
      <>
        <ButtonWithTrackEvent
          {...cancelButtonEventProperties}
          __cssFor={{ root: buttonCss }}
          onClick={() => handleClick("cancelReport")}
          flavor={Button.flavors.secondary}
        >
          Cancel
        </ButtonWithTrackEvent>
        <ButtonWithTrackEvent
          {...submitButtonEventProperties}
          __cssFor={{ root: buttonCss }}
          onClick={() => handleClick("submit")}
          flavor={Button.flavors.primary}
        >
          Submit report
        </ButtonWithTrackEvent>
      </>
    );
  };

  return <>{isReportSubmitted ? renderReturnButton() : renderFormOptions()}</>;
};

const PowerWordsNavigationButtons = ({
  currentQuestion,
  handleClick,
  objectId,
  contentId,
  isStudent,
  isPreviewMode,
  isSubmitted,
  isSummary,
  questionCount,
}) => {
  const powerWordsLastQuestion =
    questionCount && currentQuestion === questionCount.toString();
  const isOnFirstQuestion = currentQuestion === "1";

  const navigationButtonEventProperties = {
    objectType: objectTypes.articleWord,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate through each power word question.",
      object_id: objectId,
      content_id: contentId,
    },
  };

  const handlePowerWordNavigation = () => {
    if (isSubmitted) {
      if (isSummary) {
        handleClick("returnToActivities");
      } else if (powerWordsLastQuestion) {
        handleClick("summary");
      } else {
        handleClick("continue");
      }
    } else {
      if (isSummary) {
        handleClick("returnToActivities");
      } else {
        handleClick("submit");
      }
    }
  };

  const previewModeButtonsContainer = () => {
    if (!powerWordsLastQuestion) {
      return (
        <>
          <ButtonWithTrackEvent
            {...navigationButtonEventProperties}
            streamProperties={navigationButtonEventProperties.streamProperties}
            __cssFor={{ root: buttonCss }}
            onClick={() => handleClick("back")}
            flavor={Button.flavors.secondary}
            disabled={isOnFirstQuestion}
          >
            Back
          </ButtonWithTrackEvent>
          <ButtonWithTrackEvent
            {...navigationButtonEventProperties}
            streamProperties={navigationButtonEventProperties.streamProperties}
            __cssFor={{ root: buttonCss }}
            onClick={() =>
              powerWordsLastQuestion
                ? handleClick("score")
                : handleClick("next")
            }
            flavor={Button.flavors.primary}
          >
            Next
          </ButtonWithTrackEvent>
        </>
      );
    }

    return (
      <ButtonWithTrackEvent
        {...navigationButtonEventProperties}
        streamProperties={navigationButtonEventProperties.streamProperties}
        __cssFor={{ root: buttonCss }}
        onClick={() => handleClick("returnToActivities")}
        flavor={Button.flavors.primary}
      >
        Return to activities
      </ButtonWithTrackEvent>
    );
  };

  const studentModeButtonsContainer = () => (
    <ButtonWithTrackEvent
      {...navigationButtonEventProperties}
      streamProperties={navigationButtonEventProperties.streamProperties}
      __cssFor={{ root: buttonCss }}
      onClick={() => handlePowerWordNavigation()}
      flavor={Button.flavors.primary}
    >
      {isSummary ? "Return to activities" : isSubmitted ? "Continue" : "Submit"}
    </ButtonWithTrackEvent>
  );

  return (
    <>
      {isPreviewMode && !isStudent
        ? previewModeButtonsContainer()
        : studentModeButtonsContainer()}
    </>
  );
};

const WritePromptNavigationButtons = ({
  handleClick,
  isConfirmationScreen,
  hasRelevantWriteResponses,
  isDisabled,
  objectId,
  isSubmittedAndReviewed,
  contentId,
  showBackToClassesButton,
}) => {
  const navigationButtonEventProperties = {
    objectType: objectTypes.writePrompt,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: "Navigate to and from the write prompt confirmation screen",
      object_id: objectId,
      content_id: contentId,
    },
  };

  return (
    <>
      {!isConfirmationScreen &&
        (!hasRelevantWriteResponses || isSubmittedAndReviewed) && (
          <>
            {showBackToClassesButton && (
              <ButtonWithTrackEvent
                {...navigationButtonEventProperties}
                __cssFor={{ root: buttonCss }}
                onClick={() => handleClick("back")}
                flavor={Button.flavors.secondary}
              >
                Back
              </ButtonWithTrackEvent>
            )}
            <ButtonWithTrackEvent
              {...navigationButtonEventProperties}
              __cssFor={{ root: buttonCss }}
              disabled={isDisabled}
              onClick={() => handleClick("next")}
              flavor={Button.flavors.primary}
            >
              Next
            </ButtonWithTrackEvent>
          </>
        )}
      {isConfirmationScreen && (
        <>
          <ButtonWithTrackEvent
            {...navigationButtonEventProperties}
            __cssFor={{ root: buttonCss }}
            onClick={() => handleClick("edit")}
            flavor={Button.flavors.secondary}
          >
            Edit
          </ButtonWithTrackEvent>
          <ButtonWithTrackEvent
            {...navigationButtonEventProperties}
            __cssFor={{ root: buttonCss }}
            onClick={() => handleClick("submit")}
            flavor={Button.flavors.primary}
          >
            Submit
          </ButtonWithTrackEvent>
        </>
      )}
      {hasRelevantWriteResponses &&
        !isSubmittedAndReviewed &&
        showBackToClassesButton && (
          <ButtonWithTrackEvent
            {...navigationButtonEventProperties}
            __cssFor={{ root: buttonCss }}
            onClick={() => handleClick("back")}
            flavor={Button.flavors.primary}
          >
            Back to classes
          </ButtonWithTrackEvent>
        )}
    </>
  );
};

NavigationButtons.propTypes = propTypes;
PowerWordsNavigationButtons.propTypes = powerWordsNavigationPropTypes;
QuizNavigationButtons.propTypes = quizNavigationPropTypes;
QuestionFlaggingNavigationButtons.propTypes =
  questionFlaggingNavigationPropTypes;
WritePromptNavigationButtons.propTypes = writePromptNavigationPropTypes;

NavigationButtons.PowerWordsNavigationButtons = PowerWordsNavigationButtons;
NavigationButtons.QuizNavigationButtons = QuizNavigationButtons;
NavigationButtons.QuestionFlaggingNavigationButtons =
  QuestionFlaggingNavigationButtons;
NavigationButtons.WritePromptNavigationButtons = WritePromptNavigationButtons;

export default NavigationButtons;
